'use client';

import { useEffect } from 'react';

import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { Box } from '#/components/Box/Box';
import { InternshipDetailsList } from '#/components/DetailsList/Internship/InternshipDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { DynamicListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/DynamicListingViewAdminTools';
import { ListingViewAppliedMessage } from '#/modules/listing/components/ListingView/AppliedMessage/ListingViewAppliedMessage';
import { ListingViewApplyAts } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAts';
import { ListingViewApplyAtsInstructions } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAtsInstructions';
import { ListingViewApplyExternal } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyExternal';
import { ListingViewIdealistAtsUserApplicationForm } from '#/modules/listing/components/ListingView/AtsApplicationForm/ListingViewIdealistAtsUserApplicationForm';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from '#/modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewBenefitsSection } from '#/modules/listing/components/ListingView/sections/ListingViewBenefitsSection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarImage } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { listingTiming } from '#/modules/listing/helpers/listingTiming';
import { useListingApplicationSubmission } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationSubmission';
import { useSaveListingAndOrg } from '#/modules/listing/hooks/useSaveListingAndOrg';
import type { ApiInternship } from '#/modules/listing/internships/api/types/ApiInternship';
import { ViewInternshipActionButtons } from '#/modules/listing/internships/components/ActionButtons/ViewInternshipActionButtons';
import { getInternshipApplySectionTitle } from '#/modules/listing/internships/helpers/getInternshipApplySectionTitle';
import type { ListingTmpStorageListingApplicationFailure } from '#/modules/listing/types/ListingTmpStorageListingApplicationFailure';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { useUserDashboardOrgInitiallySaved } from '#/modules/userDashboard/hooks/useUserDashboardOrgInitiallySaved';
import type { DatetimeString } from '#/types/TypedDateString';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { isExternalUrl } from '#/utils/url/isExternalUrl';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  internship: ApiInternship;
  listingApplicationId: ApiListingApplicationId | null;
  initiallySaved: boolean;
  listingApplicationFailure: ListingTmpStorageListingApplicationFailure | null;
  now: DatetimeString;
};

export function ViewInternshipPage({
  internship,
  listingApplicationId,
  initiallySaved,
  listingApplicationFailure,
  now,
}: Props) {
  const { i18n, locale } = useI18n();

  const [applied, setApplied] = useKeyedState(Boolean(listingApplicationId));

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, internship.org?.id);

  const { searchContext } = useSearchContextHeader({
    currentObjectId: internship.id,
  });

  useEffect(() => {
    trackViewListing({
      status: 'published',
      hasAts: internship.hasAts,
      listingType: internship.type,
      listingId: internship.id,
      listingName: internship.name,
      orgId: internship.org?.id,
      orgName: internship.org?.name,
      orgType: internship.org?.orgType,
    });
  }, [internship]);

  const orgId = internship.org?.id ?? null;
  const orgType = internship.org?.orgType ?? null;
  const orgLogo = internship.orgLogoOverride ?? internship.org?.logo ?? null;
  const orgName = internship.orgNameOverride ?? internship.org?.name ?? null;
  const orgUrl = internship.org?.url[locale] ?? null;

  const { orgInitiallySaved } = useUserDashboardOrgInitiallySaved({
    orgId: internship.org?.id,
  });

  const timing = listingTiming({
    now,
    expires: internship.expires,
    publishedDate: internship.firstPublished,
    isPreview: false,
  });

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: internship,
    orgInitiallySaved,
    initiallySaved,
    searchSavedListingsStore: null,
  });

  const {
    listingApplicationInitialValues,
    listingApplicationInitialErrors,
    submitListingApplication,
  } = useListingApplicationSubmission({
    listingId: internship.id,
    listingType: internship.type,
    applied,
    onApplied: () => setApplied(true),
    orgId: internship.org?.id ?? null,
    orgType: internship.org?.orgType ?? null,
    listingApplicationFailure,
  });

  return (
    <>
      {isOrgOwner && internship.org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this internship')}
          tools={
            <DynamicListingViewAdminTools
              listingId={internship.id}
              listingType="INTERNSHIP"
              orgId={internship.org.id}
              hasAts={internship.hasAts}
              canPost
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={internship.address}
            isPostedAnonymously={internship.isPostedAnonymously}
            listingId={internship.id}
            listingName={internship.name}
            listingType={internship.type}
            timing={timing}
            orgName={orgName}
            orgType={internship.org?.orgType ?? null}
            orgUrl={orgUrl}
            remoteOptions={internship.remoteOptions}
            actionButtons={
              <ViewInternshipActionButtons
                internship={internship}
                applied={applied}
                internshipSaved={saved}
                onSaveInternship={save}
                onUnsaveInternship={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <InternshipDetailsList
              academicCredits={internship.academicCredits}
              startDate={internship.startDate}
              endDate={internship.endDate}
              applicationDeadline={internship.applicationDeadline}
              payment={internship.payment}
              wage={internship.wage}
              hoursPerWeek={internship.hoursPerWeek}
              areasOfFocus={internship.areasOfFocus}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={internship.name}
            image={null}
            description={internship.description}
            descriptionTruncated={internship.descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalUrl(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: internship.functions,
                  orgId: internship.org?.id,
                  listingName: internship.name,
                  listingType: 'INTERNSHIP',
                });
              }
            }}
            showMoreLinkTitle={i18n('Read more about this internship')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: internship.type,
                locale: internship.locale,
              });
            }}
          />

          <ListingViewBenefitsSection
            benefits={internship.benefits}
            benefitsTruncated={internship.benefitsTruncated}
          />

          <ListingViewLocationSection
            listingType="INTERNSHIP"
            address={internship.address}
            remoteOptions={internship.remoteOptions}
          />

          <ListingViewApplySection
            title={getInternshipApplySectionTitle({
              i18n,
              hasAts: internship.hasAts,
            })}
          >
            {internship.hasAts ? (
              // oxlint-disable-next-line react/jsx-no-useless-fragment
              <>
                {applied ? (
                  <ListingViewAppliedMessage />
                ) : (
                  <ListingViewApplyAts
                    applyText={
                      <ListingViewApplyAtsInstructions
                        applyText={internship.applyText}
                        locale={locale}
                      />
                    }
                    applicationForm={
                      <ListingViewIdealistAtsUserApplicationForm
                        listingId={internship.id}
                        listingType="INTERNSHIP"
                        requestedAttachments={internship.requestedAttachments}
                        submission={{
                          listingApplicationInitialValues,
                          listingApplicationInitialErrors,
                          onSubmit: submitListingApplication,
                          requestedAttachmentsHash:
                            internship.requestedAttachmentsHash,
                        }}
                      />
                    }
                    trackReached={{
                      listingId: internship.id,
                      listingType: 'INTERNSHIP',
                      orgId,
                      orgType,
                      applied,
                    }}
                  />
                )}
              </>
            ) : (
              <ListingViewApplyExternal
                applyEmail={internship.applyEmail}
                applyText={internship.applyText}
                applyTextTruncated={internship.applyTextTruncated}
                applyUrl={internship.applyUrl}
                applied={applied}
                onMarkAsApplied={() => Promise.resolve()}
                functions={internship.functions}
                listingName={internship.name}
                listingType="INTERNSHIP"
                listingId={internship.id}
                orgId={orgId}
                orgType={orgType}
                synonym={null}
                isPreview={false}
              />
            )}
          </ListingViewApplySection>

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            orgLogo={orgLogo}
            orgName={orgName}
            orgURLForCurrentLocale={orgUrl}
          />
          <ListingViewSidebarTitle
            address={internship.address}
            listingName={internship.name}
            listingType="INTERNSHIP"
            orgName={orgName}
            orgUrl={orgUrl}
            remoteOptions={internship.remoteOptions}
          />
          <ListingViewSidebarContent
            timing={timing}
            listingType={internship.type}
            showScrollToApplyButton={internship.hasAts}
            applied={applied}
            listingViewStatus={ListingViewStatus.PUBLISHED}
          />
          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={orgUrl}
          />
        </ListingViewSidebar>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta
              listingUrl={internship.url[locale]}
              type={internship.type}
            />
          </ListingViewFooter>
        </PrintHidden>
      )}
    </>
  );
}
